import { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { Paperclip, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import {
  MILESTONE_NA_STYLE,
  MILESTONE_STATE_RANK,
  SectionCard,
  milestoneMeta,
} from "@/features/power_projects/components/ui";
import type { MilestoneState } from "@/features/power_projects/types";
import * as api from "../api";
import { useLedMutation, useOrdersPicker, useTiles } from "../hooks";
import type { LedSide, TileRequestRow, WorkTile } from "../types";
import { ConfirmInline, Empty, ErrorLine, Field, FilePick, INPUT, INPUT_SM, OnlyUs, ReasonBox, SideTag, TONE_WAIT, fmtDate, today } from "./common";

interface Props {
  leadershipId: string;
  internal: boolean;
  contractorName: string;
}

// ── zmiana stanu: ta sama mechanika co na tablicy kamieni (badge → lista stanów) ──
function StatePopover({ tile, states, canMove, onPick }: { tile: WorkTile; states: { code: string; label: string }[]; canMove: boolean; onPick: (state: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const meta = milestoneMeta(tile.state as MilestoneState);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); window.removeEventListener("keydown", esc); };
  }, [open]);
  if (!canMove) return <Badge color={meta.color}>{meta.label}</Badge>;
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="group inline-flex items-center gap-1" title="Zmień stan" aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Badge color={meta.color}>{meta.label}</Badge>
        <span className="text-[9px] leading-none text-[var(--color-text-muted)] transition-transform group-hover:text-[var(--color-primary)]" style={{ transform: open ? "rotate(180deg)" : undefined }} aria-hidden>▾</span>
      </button>
      {open && (
        <div role="listbox" className="absolute left-0 top-full z-20 mt-1 min-w-[12rem] border border-[var(--color-border)] bg-[var(--color-bg-surface)] py-1 shadow-lg">
          {states.map((s) => {
            const m = milestoneMeta(s.code as MilestoneState);
            return (
              <button
                key={s.code}
                type="button"
                role="option"
                aria-selected={s.code === tile.state}
                className={clsx("flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-[var(--color-bg-elevated)]", s.code === tile.state && "bg-[var(--color-bg-elevated)] font-semibold")}
                onClick={() => { setOpen(false); if (s.code !== tile.state) onPick(s.code); }}
              >
                <span className="h-2 w-2 shrink-0" style={{ background: m.accent }} aria-hidden />
                {s.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AssignDialog({ leadershipId, tile, open, onClose }: { leadershipId: string; tile: WorkTile; open: boolean; onClose: () => void }) {
  const { data: picker } = useOrdersPicker(leadershipId, open);
  const [mode, setMode] = useState<"order" | "contractor" | "none">(tile.assigned_order_id ? "order" : tile.assigned_contractor_id ? "contractor" : "none");
  const [order, setOrder] = useState(tile.assigned_order_id ?? "");
  const [contractor, setContractor] = useState(tile.assigned_contractor_id ?? "");
  const [note, setNote] = useState(tile.internal_note ?? "");
  const assign = useLedMutation(leadershipId, () => api.assignTile(leadershipId, tile.id, { order: mode === "order" ? order || null : null, contractor: mode === "contractor" ? contractor || null : null, internal_note: note }), ["tiles"]);
  return (
    <Modal open={open} onClose={onClose} title={`Komu zlecamy: ${tile.label}`} size="md">
      <OnlyUs className="mb-3">Firma prowadząca widzi tylko „po stronie GK ELEKTIM” — ani zamówienia, ani wykonawcy, ani tej notatki.</OnlyUs>
      <div className="space-y-2 text-[13px]">
        <label className="flex items-center gap-2"><input type="radio" checked={mode === "order"} onChange={() => setMode("order")} /> istniejące zamówienie na tym przedsięwzięciu</label>
        {mode === "order" && (
          <select className={INPUT} value={order} onChange={(e) => setOrder(e.target.value)}>
            <option value="">— wybierz —</option>
            {(picker?.orders ?? []).map((o) => <option key={o.id} value={o.id}>{o.order_number} · {o.contractor_name} · {o.order_type_display} · {o.status_display}</option>)}
          </select>
        )}
        <label className="flex items-center gap-2"><input type="radio" checked={mode === "contractor"} onChange={() => setMode("contractor")} /> firma z kartoteki (bez zamówienia)</label>
        {mode === "contractor" && (
          <select className={INPUT} value={contractor} onChange={(e) => setContractor(e.target.value)}>
            <option value="">— wybierz —</option>
            {(picker?.contractors ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <label className="flex items-center gap-2"><input type="radio" checked={mode === "none"} onChange={() => setMode("none")} /> robimy sami / jeszcze nie zlecone</label>
        <Field label="Notatka wewnętrzna"><input className={INPUT} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
      <ErrorLine error={assign.error} />
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>Anuluj</Button>
        <Button disabled={assign.isPending} onClick={async () => { await assign.mutateAsync(); onClose(); }}>Zapisz</Button>
      </div>
    </Modal>
  );
}

type Panel = "none" | "reason" | "edit" | "note" | "delete";

/** Kafel — wyglądem to samo co kamień na tablicy DLC 01: lewa sztaba w kolorze stanu,
 *  tło w odcieniu stanu, badge ze stanem otwiera listę. */
function TileCard({ leadershipId, tile, states, internal, contractorName }: { leadershipId: string; tile: WorkTile; states: { code: string; label: string }[]; internal: boolean; contractorName: string }) {
  const [assigning, setAssigning] = useState(false);
  const [panel, setPanel] = useState<Panel>("none");
  const [wanted, setWanted] = useState<string | null>(null);
  const [label, setLabel] = useState(tile.label);
  const [due, setDue] = useState(tile.due_date ?? "");
  const [side, setSide] = useState<LedSide | "">(tile.side ?? "");
  const setState = useLedMutation(leadershipId, (a: { state: string; note: string }) => api.setTileState(leadershipId, tile.id, { state: a.state, note: a.note, reason: a.note }), ["tiles"]);
  const upload = useLedMutation(leadershipId, (files: File[]) => api.uploadTileFiles(leadershipId, tile.id, files), ["tiles"]);
  const removeFile = useLedMutation(leadershipId, (id: number) => api.removeTileFile(leadershipId, id), ["tiles"]);
  const patch = useLedMutation(leadershipId, (body: { label?: string; due_date?: string | null; side?: string | null; delete?: boolean }) => api.patchTile(leadershipId, tile.id, body), ["tiles"]);
  const addNote = useLedMutation(leadershipId, (note: string) => api.addEntry(leadershipId, { kind: "note", occurred_on: today(), body: note, milestone: tile.id }), ["tiles"]);
  const meta = milestoneMeta(tile.state as MilestoneState);
  const na = tile.state === "na";
  const overdue = !!tile.due_date && !na && tile.state !== "done" && tile.due_date < today();
  const toggle = (p: Panel) => setPanel((cur) => (cur === p ? "none" : p));

  /** Cofnięcie stanu wymaga powodu (tak samo jak na tablicy kamieni). */
  const pickState = (next: string) => {
    const back = (MILESTONE_STATE_RANK[next as MilestoneState] ?? 0) < (MILESTONE_STATE_RANK[tile.state as MilestoneState] ?? 0);
    setWanted(next);
    if (back) setPanel("reason");
    else { setState.mutate({ state: next, note: "" }); setPanel("none"); }
  };

  return (
    <div
      className="card flex flex-col gap-2 p-3"
      style={{
        borderLeft: `3px solid ${na ? "#4b5563" : meta.accent}`,
        ...(na ? MILESTONE_NA_STYLE : { background: `linear-gradient(0deg, ${meta.tint}, ${meta.tint}), var(--color-bg-surface)` }),
      }}
    >
      <div className="flex min-w-0 items-start gap-1.5">
        <span className="mt-1 h-2 w-2 shrink-0" style={{ background: meta.accent }} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[0.72rem] font-bold uppercase leading-tight tracking-wide text-[var(--color-text-primary)]" title={tile.label}>{tile.label}</div>
          <div className="mt-0.5 text-[11px] text-[var(--color-text-secondary)]">
            {tile.due_date ? <span className={clsx(overdue && "font-bold text-[var(--color-primary)]")}>termin {fmtDate(tile.due_date)}</span> : <span className="text-[var(--color-text-muted)]">bez terminu</span>}
            {tile.completed_at && <> · gotowe {fmtDate(tile.completed_at)}</>}
          </div>
        </div>
        {tile.is_custom && <span className="shrink-0 border border-[var(--color-border)] px-1 font-mono text-[8px] uppercase leading-tight tracking-wide text-[var(--color-text-muted)]" title="Kafel dodany ręcznie">własny</span>}
      </div>

      {/* „Nie dotyczy” wybiera także firma na swoim kafelku; kafel zostaje po jej stronie,
          więc lista stanów działa i po zamknięciu — powrót to cofnięcie (z powodem). */}
      <div className="self-start"><StatePopover tile={tile} states={states} canMove={tile.can_move} onPick={pickState} /></div>
      {!tile.can_move && !na && <div className="-mt-1 text-[11px] text-[var(--color-text-muted)]">stan zmienia GK ELEKTIM</div>}

      {internal && (tile.assigned_contractor_name || tile.assigned_order_number || tile.internal_note) && (
        <OnlyUs className="!px-2 !py-1 text-[11px]">zlecone: {[tile.assigned_order_number, tile.assigned_contractor_name, tile.internal_note].filter(Boolean).join(" · ")}</OnlyUs>
      )}

      {tile.files.length > 0 && (
        <ul className="space-y-0.5 text-[11px]">
          {tile.files.map((f) => (
            <li key={f.tile_file_id} className="flex items-center gap-1.5">
              <Paperclip size={11} className="shrink-0 text-[var(--color-text-muted)]" />
              <span className="min-w-0 flex-1 truncate"><ManagedFileLink file={{ url: f.url, original_filename: f.name }}>{f.name}</ManagedFileLink></span>
              <button type="button" className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" title={`Usuń plik (dodał ${f.uploaded_by_label})`} onClick={() => removeFile.mutate(f.tile_file_id)}><X size={11} /></button>
            </li>
          ))}
        </ul>
      )}

      {tile.entries.length > 0 && (
        <ul className="space-y-1 border-t border-dashed border-[var(--color-border-muted)] pt-1.5 text-[11px]">
          {tile.entries.slice(0, 4).map((e) => (
            <li key={e.id} className={clsx(e.withdrawn && "line-through opacity-60")}>
              <span className="font-mono text-[var(--color-text-muted)]">{fmtDate(e.occurred_on)}</span> <SideTag side={e.side} contractorName={contractorName} /> {e.body}
            </li>
          ))}
          {tile.entries.length > 4 && <li className="text-[var(--color-text-muted)]">…i {tile.entries.length - 4} starszych</li>}
        </ul>
      )}

      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-dashed border-[var(--color-border-muted)] pt-1.5 text-[11px]">
        {!na && <FilePick onFiles={(files) => upload.mutate(files)} label="Plik" busy={upload.isPending} />}
        {!na && <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" onClick={() => toggle("note")}>+ uwaga</button>}
        {internal && !na && (tile.side === "gk" || !tile.side) && (
          <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" onClick={() => setAssigning(true)}>komu zlecamy</button>
        )}
        {internal && <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" onClick={() => toggle("edit")}>edytuj</button>}
        {internal && (tile.is_custom ? (
          <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" onClick={() => toggle("delete")}>usuń</button>
        ) : na ? (
          <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" disabled={setState.isPending} onClick={() => setState.mutate({ state: "not_started", note: "przywrócony" })}>przywróć</button>
        ) : (
          <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" onClick={() => toggle("delete")}>nie dotyczy</button>
        ))}
      </div>

      <ErrorLine error={setState.error || patch.error || upload.error || removeFile.error || addNote.error} />
      {panel === "reason" && wanted && (
        <ReasonBox
          label={`Cofasz stan na „${milestoneMeta(wanted as MilestoneState).label}" — podaj powód`}
          danger
          confirmLabel="Zapisz stan"
          busy={setState.isPending}
          onCancel={() => { setPanel("none"); setWanted(null); }}
          onConfirm={async (note) => { await setState.mutateAsync({ state: wanted, note }); setPanel("none"); setWanted(null); }}
        />
      )}
      {panel === "note" && (
        <ReasonBox label="Uwaga do kafla" confirmLabel="Dodaj uwagę" busy={addNote.isPending} onCancel={() => setPanel("none")} onConfirm={async (note) => { await addNote.mutateAsync(note); setPanel("none"); }} />
      )}
      {panel === "edit" && internal && (
        <div className="grid grid-cols-1 gap-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-2 sm:grid-cols-2">
          <Field label="Nazwa" className="sm:col-span-2"><input className={INPUT_SM} value={label} onChange={(e) => setLabel(e.target.value)} /></Field>
          <Field label="Termin"><DatePicker value={due} className="!py-1 text-[12px]" onChange={setDue} /></Field>
          <Field label="Po czyjej stronie">
            <select className={INPUT_SM} value={side} onChange={(e) => setSide(e.target.value as LedSide | "")}>
              <option value="">— nieustalona —</option>
              <option value="gk">GK ELEKTIM</option>
              <option value="contractor">firma prowadząca</option>
            </select>
          </Field>
          <div className="flex items-end justify-end gap-2 sm:col-span-2">
            <Button size="sm" variant="ghost" onClick={() => setPanel("none")}>Anuluj</Button>
            <Button size="sm" disabled={patch.isPending || !label.trim()} onClick={async () => { await patch.mutateAsync({ label, due_date: due || null, side: side || null }); setPanel("none"); }}>Zapisz</Button>
          </div>
        </div>
      )}
      {panel === "delete" && (
        <ConfirmInline
          question={tile.is_custom ? "Usunąć ten kafel?" : "Oznaczyć jako nie dotyczy?"}
          yesLabel={tile.is_custom ? "Usuń" : "Nie dotyczy"}
          busy={patch.isPending}
          onYes={async () => { await patch.mutateAsync({ delete: true }); setPanel("none"); }}
          onNo={() => setPanel("none")}
        />
      )}
      {internal && <AssignDialog leadershipId={leadershipId} tile={tile} open={assigning} onClose={() => setAssigning(false)} />}
    </div>
  );
}

function Requests({ leadershipId, requests, internal }: { leadershipId: string; requests: TileRequestRow[]; internal: boolean }) {
  const decide = useLedMutation(leadershipId, (a: { id: string; accept: boolean; side: string | null; note: string; due: string }) => api.decideTileRequest(leadershipId, a.id, { accept: a.accept, side: a.side, note: a.note, due_date: a.due || null }), ["tiles"]);
  const [side, setSide] = useState<Record<string, string>>({});
  const [due, setDue] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState<string | null>(null);
  if (!requests.length) return null;
  const pending = requests.filter((r) => r.status === "pending");
  const decided = requests.filter((r) => r.status !== "pending");
  return (
    <div className={clsx("mb-4 border", pending.length ? TONE_WAIT : "border-[var(--color-border)] bg-[var(--color-bg-surface)]")}>
      <div className="border-b border-[var(--color-border)] px-4 py-2 font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">
        Prośby firmy o nowy kafel{pending.length > 0 && ` · ${pending.length} do rozstrzygnięcia`}
      </div>
      <ul className="divide-y divide-[var(--color-border-muted)]">
        {[...pending, ...decided].map((r) => (
          <li key={r.id} className="px-4 py-2 text-[13px]">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <strong>{r.label}</strong>
              {r.reason && <span className="text-[var(--color-text-secondary)]">{r.reason}</span>}
              <span className="text-[11px] text-[var(--color-text-muted)]">{r.requested_by_label} · {fmtDate(r.requested_at)}</span>
              <Badge color={r.status === "pending" ? "yellow" : r.status === "accepted" ? "green" : "red"}>{r.status_display}</Badge>
              {r.decision_note && <span className="text-xs">„{r.decision_note}”</span>}
            </div>
            {internal && r.status === "pending" && rejecting !== r.id && (
              <div className="mt-2 flex flex-wrap items-end gap-2 border-t border-dashed border-[var(--color-border-muted)] pt-2">
                <label className="inline-flex items-center gap-1.5 text-[12px]">
                  <span className="section-label">strona</span>
                  <select className={clsx(INPUT_SM, "w-auto")} value={side[r.id] ?? "contractor"} onChange={(e) => setSide({ ...side, [r.id]: e.target.value })}>
                    <option value="contractor">firmy prowadzącej</option>
                    <option value="gk">GK ELEKTIM</option>
                  </select>
                </label>
                <label className="inline-flex items-center gap-1.5 text-[12px]">
                  <span className="section-label">termin</span>
                  <DatePicker value={due[r.id] ?? ""} className="!py-1 w-[9.5rem] text-[12px]" onChange={(v) => setDue({ ...due, [r.id]: v })} />
                </label>
                <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, accept: true, side: side[r.id] ?? "contractor", note: "", due: due[r.id] ?? "" })}>Dodaj kafel</Button>
                <Button size="sm" variant="ghost" disabled={decide.isPending} onClick={() => setRejecting(r.id)}>Odrzuć…</Button>
              </div>
            )}
            {rejecting === r.id && (
              <ReasonBox label="Powód odrzucenia (firma go zobaczy)" danger confirmLabel="Odrzuć prośbę" busy={decide.isPending} onCancel={() => setRejecting(null)} onConfirm={async (note) => { await decide.mutateAsync({ id: r.id, accept: false, side: null, note, due: "" }); setRejecting(null); }} />
            )}
          </li>
        ))}
      </ul>
      <ErrorLine error={decide.error} className="px-4 pb-2" />
    </div>
  );
}

/** Uzgodnienia (kafle) — stan, pliki, uwagi; firma rusza tylko swoje kafle i może
 *  prosić o nowe; my dodajemy, edytujemy, usuwamy i zapisujemy „komu zlecamy”. */
export function TilesTab({ leadershipId, internal, contractorName }: Props) {
  const { data, isLoading, error } = useTiles(leadershipId);
  const [requesting, setRequesting] = useState(false);
  const [reqLabel, setReqLabel] = useState("");
  const [reqReason, setReqReason] = useState("");
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newSide, setNewSide] = useState<LedSide>("contractor");
  const [newDue, setNewDue] = useState("");
  const [showNa, setShowNa] = useState(false);
  const request = useLedMutation(leadershipId, () => api.requestTile(leadershipId, { label: reqLabel, reason: reqReason }), ["tiles"]);
  const add = useLedMutation(leadershipId, () => api.addTile(leadershipId, { label: newLabel, side: newSide, due_date: newDue || null }), ["tiles"]);
  if (isLoading || !data) return <div className="flex h-40 items-center justify-center">{error ? <ErrorLine error={error} /> : <Spinner />}</div>;
  const live = data.tiles.filter((t) => t.state !== "na");
  const na = data.tiles.filter((t) => t.state === "na");
  const mine = live.filter((t) => t.side === "contractor");
  const theirs = live.filter((t) => t.side === "gk");
  const unset = live.filter((t) => !t.side);
  const groups: { title: string; items: WorkTile[] }[] = internal
    ? [
        { title: "Po stronie firmy prowadzącej", items: mine },
        { title: "Po stronie GK ELEKTIM", items: theirs },
        { title: "Strona nieustalona", items: unset },
      ]
    : [
        { title: "Wasze kafle — stan zmieniacie Wy", items: mine },
        { title: "Po stronie GK ELEKTIM — możecie dodać plik i uwagę", items: [...theirs, ...unset] },
      ];

  return (
    <div className="space-y-4">
      <Requests leadershipId={leadershipId} requests={data.requests} internal={internal} />

      {!internal && (requesting ? (
        <div className="flex flex-wrap items-end gap-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3">
          <Field label="Nazwa kafla" className="min-w-[16rem]"><input className={INPUT} autoFocus value={reqLabel} onChange={(e) => setReqLabel(e.target.value)} placeholder="np. Opinia konserwatora zabytków" /></Field>
          <Field label="Dlaczego (opcjonalnie)" className="min-w-[16rem] flex-1"><input className={INPUT} value={reqReason} onChange={(e) => setReqReason(e.target.value)} /></Field>
          <Button size="sm" disabled={!reqLabel.trim() || request.isPending} onClick={async () => { await request.mutateAsync(); setReqLabel(""); setReqReason(""); setRequesting(false); }}>Wyślij prośbę</Button>
          <Button size="sm" variant="ghost" onClick={() => setRequesting(false)}>Anuluj</Button>
          <ErrorLine error={request.error} />
        </div>
      ) : (
        <Button size="sm" variant="secondary" onClick={() => setRequesting(true)}>Poproś GK ELEKTIM o nowy kafel</Button>
      ))}

      {internal && (adding ? (
        <div className="flex flex-wrap items-end gap-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-3">
          <Field label="Nazwa kafla" className="min-w-[16rem] flex-1"><input className={INPUT} autoFocus value={newLabel} onChange={(e) => setNewLabel(e.target.value)} /></Field>
          <Field label="Po czyjej stronie">
            <select className={INPUT} value={newSide} onChange={(e) => setNewSide(e.target.value as LedSide)}>
              <option value="contractor">firma prowadząca</option>
              <option value="gk">GK ELEKTIM</option>
            </select>
          </Field>
          <Field label="Termin"><DatePicker value={newDue} onChange={setNewDue} /></Field>
          <Button size="sm" disabled={!newLabel.trim() || add.isPending} onClick={async () => { await add.mutateAsync(); setNewLabel(""); setNewDue(""); setAdding(false); }}>Dodaj kafel</Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Anuluj</Button>
          <ErrorLine error={add.error} />
        </div>
      ) : (
        <Button size="sm" onClick={() => setAdding(true)}>+ kafel</Button>
      ))}

      {groups.filter((g) => g.items.length > 0 || g.title.startsWith("Wasze")).map((g) => (
        <SectionCard key={g.title} title={`${g.title} · ${g.items.length}`} bodyClassName="p-3">
          {g.items.length === 0 ? (
            <Empty>Brak kafli w tej grupie.</Empty>
          ) : (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {g.items.map((t) => <TileCard key={t.id} leadershipId={leadershipId} tile={t} states={data.states} internal={internal} contractorName={contractorName} />)}
            </div>
          )}
        </SectionCard>
      ))}

      {na.length > 0 && (
        <div>
          <button type="button" className="section-label hover:text-[var(--color-primary)]" onClick={() => setShowNa((v) => !v)}>
            {showNa ? "▾" : "▸"} nie dotyczy · {na.length}
          </button>
          {showNa && (
            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {na.map((t) => <TileCard key={t.id} leadershipId={leadershipId} tile={t} states={data.states} internal={internal} contractorName={contractorName} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
